import React, { useContext, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { DirectionContext } from "../App";

const CAPTURE_URL = "https://m.duo.co.kr/personalinfo/";

const AdditionalEvidenceGuidePage = () => {
  const { navigateTo } = useContext(DirectionContext);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleCapture = () => {
    window.open(CAPTURE_URL, "_blank", "noopener,noreferrer");
  };

  const handleSubmit = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmYes = () => {
    setShowConfirmModal(false);
    navigateTo("/additionalevidence/auth");
  };

  const handleConfirmNo = () => {
    setShowConfirmModal(false);
  };

  const pageVariants = {
    initial: { opacity: 0 },
    in: { opacity: 1, transition: { duration: 0.5 } },
    out: { opacity: 0, transition: { duration: 0.3 } },
  };

  const contentVariants = {
    initial: { opacity: 0, y: 20 },
    in: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: 0.2 },
    },
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <HeroSection>
            <HeroTitle>
              듀오 개인정보 유출 확인
              <br />
              <Strong>'전체 화면'</Strong>을 제출해 주세요.
            </HeroTitle>
            <HeroDescription>
              듀오에서 공식적으로 조회 페이지를 제공합니다.
            </HeroDescription>
          </HeroSection>

          <CaptureButton
            onClick={handleCapture}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            유출 확인 페이지 이동하기
          </CaptureButton>

          <ExampleSection>
            <ExampleTitle>개인정보 유출 내역 캡처 예시</ExampleTitle>
            <ExampleImageWrapper>
              <ExampleImage
                src="/png/eximg.png"
                alt="개인정보 유출 내역 캡처 예시"
              />
            </ExampleImageWrapper>
          </ExampleSection>
        </Content>
      </Container>

      <FixedBottomBar>
        <FixedBottomInner>
          <SubmitButton
            onClick={handleSubmit}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            캡처 화면 증거 자료 제출하기
          </SubmitButton>
        </FixedBottomInner>
      </FixedBottomBar>

      <AnimatePresence>
        {showConfirmModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={handleConfirmNo}
          >
            <ModalCard
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ type: "spring", stiffness: 320, damping: 26 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalTitle>유출 목록 캡처 이미지가 준비되셨나요?</ModalTitle>
              <ModalBody>
                듀오 유출 조회 페이지에서 본인의 유출 항목이
                <br />
                모두 보이도록 <ModalStrong>캡처한 이미지</ModalStrong>가
                필요합니다.
                <br />
                준비되셨다면 <ModalStrong>예</ModalStrong>, 없으시다면{" "}
                <ModalStrong>아니오</ModalStrong>를 눌러서 유출 목록을 먼저
                캡처해주세요.
              </ModalBody>
              <ModalButtonRow>
                <ModalNoButton
                  onClick={handleConfirmNo}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  아니오
                </ModalNoButton>
                <ModalYesButton
                  onClick={handleConfirmYes}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  예
                </ModalYesButton>
              </ModalButtonRow>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </PageBackground>
  );
};

const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f7f7f7;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f7f7f7;

  @media (max-width: 768px) {
    padding: 0 0 40px 0;
    max-width: 100%;
  }

  @media (min-width: 769px) {
    border-radius: 20px;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.08);
    padding: 0 0 40px 0;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 40px 24px 120px 24px;
  box-sizing: border-box;

  @media (min-width: 769px) {
    padding: 48px 28px 120px 28px;
  }
`;

const HeroSection = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const HeroTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  line-height: 1.4;
  color: #111827;
  margin: 0 0 12px 0;
  word-break: keep-all;
`;

const Strong = styled.span`
  font-weight: 700;
  color: #111827;
`;

const HeroDescription = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #6b7280;
  margin: 0;
  word-break: keep-all;
`;

const CaptureButton = styled(motion.button)`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0 14px;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  background: white;
  color: #e31c20;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 32px;
`;

const ExampleSection = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const ExampleTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 12px 0;
`;

const ExampleImageWrapper = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 16px;
  padding: 16px;
  box-sizing: border-box;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const ExampleImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
`;

const SubmitButton = styled(motion.button)`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0 14px;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  background: #e31c20;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
`;

const FixedBottomBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  background: linear-gradient(
    to bottom,
    rgba(247, 247, 247, 0) 0%,
    rgba(247, 247, 247, 1) 30%
  );
  padding: 20px 16px calc(10px + env(safe-area-inset-bottom)) 16px;
  z-index: 50;
  pointer-events: none;
`;

const FixedBottomInner = styled.div`
  width: 100%;
  max-width: 470px;
  pointer-events: auto;

  @media (min-width: 769px) {
    padding: 0 12px;
    box-sizing: border-box;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
`;

const ModalCard = styled(motion.div)`
  width: 100%;
  max-width: 360px;
  background-color: #ffffff;
  border-radius: 20px;
  padding: 24px 22px 18px 22px;
  box-sizing: border-box;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: #2e3840;
  margin: 0 0 14px 0;
  word-break: keep-all;
`;

const ModalBody = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #000000;
  line-height: 1.6;
  margin: 0 0 22px 0;
  word-break: keep-all;
`;

const ModalStrong = styled.span`
  font-weight: 600;
`;

const ModalButtonRow = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

const ModalNoButton = styled(motion.button)`
  flex: 1;
  height: 50px;
  border-radius: 999px;
  background: #ffffff;
  color: #e31c20;
  font-size: 16px;
  font-weight: 600;
  border: 1px solid #ffe0e0;
  cursor: pointer;
`;

const ModalYesButton = styled(motion.button)`
  flex: 1;
  height: 50px;
  border-radius: 999px;
  background: #e31c20;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
`;

export default AdditionalEvidenceGuidePage;
